import { useGoogleAnalytics } from "client/hooks/utils/useGoogleAnalytics";
import { get } from "client/services/Backend/get";
import { calculateCanvasImageDimensions } from "client/utils/calculateCanvasImageDimensions";
import { getRandomId } from "client/utils/getRandomId";
import { ChangeEvent, useEffect, useState } from "react";
import { addIcon, setActiveIconId } from "store/features/memeGenerator/actions";
import { MemeIconState } from "store/features/memeGenerator/types";
import { getHighestZIndexElement } from "store/features/memeGenerator/utils/getHighestZIndexElement";
import { store, useAppDispatch } from "store/store";
import { Icon } from "types/meme";

let cachedIcons: Array<Icon> = [];

export const useIconsModal = (close: () => void) => {
    const [icons, setIcons] = useState<Array<Icon>>(cachedIcons);
    const [isFetching, setIsFetching] = useState(false);
    const [search, setSearch] = useState("");
    const filteredIcons = icons.filter((icon) => icon.title.toLowerCase().includes(search));
    const dispatch = useAppDispatch();

    async function fetchIcons() {
        if (cachedIcons.length > 0 || isFetching) return;
        setIsFetching(true);
        const { result, error } = await get<{ icons: Array<Icon> }>(`${process.env.NEXT_PUBLIC_BE_URL}/icons`);
        if (!error && result) {
            setIcons(result.icons);
            cachedIcons = result.icons;
        }
        setIsFetching(false);
    }

    const { gtag: onIconClickGtag } = useGoogleAnalytics();

    function handleIconClick(icon: Icon) {
        const meme = store.getState().memeGenerator.meme;
        if (!meme) throw new Error("Tried to add icon when no meme");
        onIconClickGtag("event", "meme_icon_selected", {
            page: "meme generator",
            meme_icon: `${icon.id}: ${icon.title}`,
        });
        const { width, height } = calculateCanvasImageDimensions(
            icon.settings.width,
            icon.settings.height,
            meme.settings.itemsContainer.width / 2,
            meme.settings.itemsContainer.height / 2,
            false,
        );
        dispatch(
            addIcon({
                id: getRandomId(),
                icon,
                settings: {
                    left: 0,
                    top: 0,
                    width,
                    height,
                    rotation: 0,
                },
                state: MemeIconState.ACTIVE,
                zIndex: (getHighestZIndexElement(store.getState().memeGenerator)?.zIndex || 0) + 1,
            }),
        );
        dispatch(setActiveIconId(icon.id));
        close();
    }

    function handleSearch(e: ChangeEvent<HTMLInputElement>) {
        setSearch(e.target.value);
    }

    function handleSearchCrossButtonClick() {
        setSearch("");
    }

    useEffect(() => {
        fetchIcons();
    }, []);

    return { filteredIcons, isFetching, handleIconClick, handleSearch, search, handleSearchCrossButtonClick };
};
